import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Alert,
    Divider,
    Switch,
    FormControlLabel,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Avatar,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import client from '../../../../../API';

const PaymentSettings = () => {
    const queryClient = useQueryClient();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const { data, isLoading } = useQuery(
        ['systemConfig'],
        () => client.systemConfig.getConfig(),
        { refetchOnWindowFocus: false }
    );

    const autoPaymentsEnabled = data?.auto_payments_enabled ?? true;
    const claimPayoutAutomationEnabled = data?.claim_payout_automation_enabled ?? true;

    const { mutate: mutateAutoPayments, isLoading: isSavingAutoPayments } = useMutation(
        (enabled) => client.systemConfig.setAutoPayments(enabled),
        {
            onSuccess: (res, enabled) => {
                queryClient.invalidateQueries(['systemConfig']);
                if (!enabled && res?.withdrawals_converted_to_manual_fallback != null) {
                    toast.success(
                        `Automatic payments disabled. ${res.withdrawals_converted_to_manual_fallback} withdrawal(s) moved to manual fallback.`
                    );
                } else {
                    toast.success('Automatic payments enabled.');
                }
            },
            onError: (err) => {
                toast.error('Failed to update payment settings.', {
                    description: err?.response?.data?.error || err.message,
                });
            },
        }
    );

    const { mutate: mutateClaimPayoutAutomation, isLoading: isSavingClaimPayoutAutomation } = useMutation(
        (enabled) => client.systemConfig.setClaimPayoutAutomation(enabled),
        {
            onSuccess: (_, enabled) => {
                queryClient.invalidateQueries(['systemConfig']);
                toast.success(
                    enabled
                        ? 'Claim-triggered payout automation enabled.'
                        : 'Claim-triggered payout automation disabled. New withdrawal claims will queue for manual admin processing.'
                );
            },
            onError: (err) => {
                toast.error('Failed to update payment settings.', {
                    description: err?.response?.data?.error || err.message,
                });
            },
        }
    );

    const isSaving = isSavingAutoPayments || isSavingClaimPayoutAutomation;

    const handleToggle = (checked) => {
        if (!checked) {
            setConfirmDialogOpen(true);
        } else {
            mutateAutoPayments(true);
        }
    };

    const handleConfirmDisable = () => {
        setConfirmDialogOpen(false);
        mutateAutoPayments(false);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Card>
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <SettingsIcon />
                        </Avatar>
                    }
                    title="Payment Processing Controls"
                    subheader="Platform-wide controls for the automatic payment system"
                />
                <Divider />
                <CardContent>
                    {/* Auto-payments cron toggle hidden — cron is manually disabled
                    <Alert severity="info" sx={{ mb: 3 }}>
                        This controls the platform-wide automatic payment cron job. When disabled,
                        the job stops running and pending automated withdrawals are moved to{' '}
                        <strong>Manual Fallback</strong> for admin processing.
                    </Alert>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Automatic Payments
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Runs every 15 minutes and processes approved automated withdrawals
                                via PayPal, Stripe, and ACH.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoPaymentsEnabled}
                                    onChange={(e) => handleToggle(e.target.checked)}
                                    disabled={isSaving}
                                    color="primary"
                                />
                            }
                            label={autoPaymentsEnabled ? 'Enabled' : 'Disabled'}
                            labelPlacement="start"
                            sx={{ ml: 2, mr: 0 }}
                        />
                    </Box>

                    {!autoPaymentsEnabled && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Automatic payments are <strong>disabled</strong>. Pending automated
                            withdrawals have been moved to <strong>Manual Fallback</strong> and
                            require admin action. Re-enabling will not automatically re-queue them.
                        </Alert>
                    )}

                    <Divider sx={{ my: 3 }} />
                    */}

                    <Alert severity="info" sx={{ mb: 3 }}>
                        This controls claim-triggered payout automation. When disabled, creator
                        and invite withdrawal claims still create withdrawal entries, but they are
                        queued for <strong>manual admin payout handling</strong> instead of being
                        auto-processed through BlitzPay.
                    </Alert>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Claim Payout Automation
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Controls creator withdraw claims, creator auto-accept-to-withdraw,
                                invite payout claims, and invite auto-resolution on invoice approval.
                            </Typography>
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={claimPayoutAutomationEnabled}
                                    onChange={(e) => mutateClaimPayoutAutomation(e.target.checked)}
                                    disabled={isSaving}
                                    color="primary"
                                />
                            }
                            label={claimPayoutAutomationEnabled ? 'Enabled' : 'Disabled'}
                            labelPlacement="start"
                            sx={{ ml: 2, mr: 0 }}
                        />
                    </Box>

                    {!claimPayoutAutomationEnabled && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Claim-triggered payout automation is <strong>disabled</strong>. New
                            withdrawal claims will be queued as manual admin payouts and will not
                            auto-send funds to PayPal, Stripe, or ACH.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Confirmation dialog — shown only when disabling */}
            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Disable Automatic Payments?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">
                        This will <strong>immediately</strong> convert all pending automated
                        withdrawals to <strong>Manual Fallback</strong>. The cron job will stop
                        processing them. Re-enabling will not auto-requeue them — admins must
                        handle each one manually.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDisable}
                        disabled={isSaving}
                    >
                        Disable
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentSettings;
